<script setup lang="ts">
  import { computed, watch } from "vue";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Switch } from "@/components/ui/switch";
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
  import { FormDialog, JsonEditorField } from "@/components/episto-ui/form-dialog";
  import { WEBHOOK_EVENT_OPTIONS, WEBHOOK_STATUS_OPTIONS } from "@utils/constants/webhookEvents";
  import type { SurveyStatus, WebhookEvent, WebhookKind } from "@/types";

  export interface WebhookForm {
    id?: number;
    name: string;
    url: string;
    event: WebhookEvent;
    target_status: SurveyStatus | null;
    delay_days: number;
    kind: WebhookKind;
    token: string;
    headers: string;
    payload: string;
    active: boolean;
  }

  const webhook = defineModel<WebhookForm>("webhook", { required: true });

  const props = defineProps<{
    open: boolean;
    isEditing?: boolean;
  }>();

  const emit = defineEmits<{
    "update:open": [value: boolean];
    save: [];
  }>();

  const kindOptions = [
    { label: "Claude routine", value: "claude_routine" },
    { label: "Custom", value: "custom" },
  ];

  const isJsonObject = (value: string) => {
    if (!value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  const validationSchema = toTypedSchema(
    z
      .object({
        name: z.string().min(1, "Please enter a name"),
        url: z.string().regex(/^https?:\/\/\S+$/, "Please enter a valid URL"),
        event: z.enum(["survey_status_changed", "half_target_reached"]),
        target_status: z.string().nullish(),
        delay_days: z.coerce.number().int().min(0, "Please enter a positive number of days"),
        kind: z.enum(["custom", "claude_routine"]),
        token: z.string().optional(),
        headers: z.string().optional(),
        payload: z.string().optional(),
        active: z.boolean(),
      })
      .superRefine((data, ctx) => {
        if (data.event === "survey_status_changed" && !data.target_status) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Please select a target status", path: ["target_status"] });
        }
        if (data.kind === "claude_routine" && !data.token?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter the routine token",
            path: ["token"],
          });
        }
        if (data.kind === "custom") {
          if (!isJsonObject(data.headers ?? "")) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid JSON format", path: ["headers"] });
          }
          if (!isJsonObject(data.payload ?? "")) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid JSON format", path: ["payload"] });
          }
        }
      }),
  );

  const { handleSubmit, values, resetForm } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues: { ...webhook.value },
  });

  watch(values, (v) => Object.assign(webhook.value, v));

  const delayHint = computed(() =>
    values.event === "half_target_reached"
      ? "0 sends the hook immediately. Otherwise it is sent this many days after the survey reaches 50% of its target."
      : "0 sends the hook immediately. Otherwise it is sent this many days later, if the survey is still in this status.",
  );

  watch(
    () => props.open,
    (open) => {
      if (open) resetForm({ values: { ...webhook.value } });
    },
  );

  const handleSave = handleSubmit(() => emit("save"));
</script>

<template>
  <FormDialog
    :open="open"
    :is-editing="isEditing"
    entity-name="Hook"
    max-width="600px"
    @update:open="emit('update:open', $event)"
    @save="handleSave">
    <FormField
      v-slot="{ componentField }"
      name="name">
      <FormItem>
        <FormLabel>Name</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="e.g. Trigger Claude routine"
            class="text-sm" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="event">
      <FormItem>
        <FormLabel>Trigger</FormLabel>
        <FormControl>
          <Select
            v-bind="componentField"
            :options="WEBHOOK_EVENT_OPTIONS" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-if="values.event === 'survey_status_changed'"
      v-slot="{ componentField }"
      name="target_status">
      <FormItem>
        <FormLabel>Target status</FormLabel>
        <FormControl>
          <Select
            v-bind="componentField"
            :options="WEBHOOK_STATUS_OPTIONS" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="delay_days">
      <FormItem>
        <FormLabel>Delay (days)</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            type="number"
            min="0"
            class="text-sm" />
        </FormControl>
        <p class="text-xs text-muted-foreground">{{ delayHint }}</p>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="kind">
      <FormItem>
        <FormLabel>Type</FormLabel>
        <FormControl>
          <Select
            v-bind="componentField"
            :options="kindOptions" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="url">
      <FormItem>
        <FormLabel>URL</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            :placeholder="
              values.kind === 'claude_routine'
                ? 'https://claude.ai/api/routines/.../trigger'
                : 'https://example.com/endpoint'
            "
            class="text-sm font-mono" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-if="values.kind === 'claude_routine'"
      v-slot="{ componentField }"
      name="token">
      <FormItem>
        <FormLabel>Token</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="Routine token"
            class="text-sm font-mono" />
        </FormControl>
        <p class="text-xs text-muted-foreground">Sent as an Authorization: Bearer header.</p>
        <FormMessage />
      </FormItem>
    </FormField>

    <template v-else>
      <FormField
        v-slot="{ componentField }"
        name="headers">
        <FormItem>
          <FormControl>
            <JsonEditorField
              id="webhook-headers"
              v-bind="componentField"
              label="Headers (JSON)" />
          </FormControl>
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="payload">
        <FormItem>
          <FormControl>
            <JsonEditorField
              id="webhook-payload"
              v-bind="componentField"
              label="Extra payload (JSON)" />
          </FormControl>
        </FormItem>
      </FormField>
    </template>

    <FormField
      v-slot="{ value, handleChange }"
      name="active">
      <FormItem>
        <FormLabel>Active</FormLabel>
        <FormControl>
          <Switch
            id="webhook-active"
            :model-value="value"
            @update:model-value="handleChange" />
        </FormControl>
      </FormItem>
    </FormField>
  </FormDialog>
</template>
